import { ExternalLink } from 'lucide-react';

import { cn } from '@/lib/utils';
import type { TrackedVehicle } from '@/types/transport';
import { ROUTE_TONE, TRIP_STATUS } from '../constants';
import { RouteMap } from './RouteMap';

export interface LiveTrackingPanelProps {
  vehicles: TrackedVehicle[];
  /** Opens the full-screen board. Given nothing, the link is not drawn. */
  onOpenFullMap?: () => void;
  onSelectVehicle?: (vehicle: TrackedVehicle) => void;
  selectedVehicleUuid?: string | null;
}

/**
 * The dashboard's tracking card: the list of what is moving, beside the map.
 *
 * The Tracking tab draws the same plate full width with its own controls — see
 * `LiveMapPanel`. Both go through `RouteMap`, so the two can never end up
 * showing routes in different colours.
 */
export function LiveTrackingPanel({
  vehicles,
  onOpenFullMap,
  onSelectVehicle,
  selectedVehicleUuid,
}: LiveTrackingPanelProps) {
  return (
    <section className="rounded-3xl border border-line bg-surface shadow-sm">
      <header className="flex items-center justify-between gap-3 border-b border-line px-5 py-4">
        <h2 className="text-base font-bold text-ink">Live vehicle tracking</h2>
        {onOpenFullMap && (
          <button
            type="button"
            onClick={onOpenFullMap}
            className="inline-flex items-center gap-1.5 rounded-pill px-2.5 py-1 text-sm font-bold text-accent transition-colors hover:bg-accent-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <span>View full map</span>
            <ExternalLink className="h-3.5 w-3.5" aria-hidden />
          </button>
        )}
      </header>

      <div className="flex flex-col gap-4 p-4 lg:flex-row">
        {/* Active vehicles, beside the plate on a desktop and above it on a phone. */}
        <div className="w-full shrink-0 rounded-2xl border border-line bg-surface-2 lg:w-64">
          <p className="border-b border-line px-3.5 py-2.5 text-sm font-bold text-ink">
            Active vehicles ({vehicles.length})
          </p>

          <ul className="flex flex-col p-1.5">
            {vehicles.map((vehicle) => {
              const isSelected = vehicle.uuid === selectedVehicleUuid;
              return (
                <li key={vehicle.uuid}>
                  <button
                    type="button"
                    onClick={() => onSelectVehicle?.(vehicle)}
                    aria-pressed={isSelected}
                    className={cn(
                      'flex w-full items-start gap-2.5 rounded-xl px-2.5 py-2 text-left transition-colors',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent',
                      isSelected ? 'bg-accent-soft' : 'hover:bg-surface-3',
                    )}
                  >
                    <span
                      className={cn('mt-1.5 h-2 w-2 shrink-0 rounded-pill', ROUTE_TONE[vehicle.tone].dot)}
                      aria-hidden
                    />
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-bold text-ink">
                        {vehicle.registrationNumber}
                      </span>
                      <span className="block truncate text-xs font-semibold text-ink-3">
                        {TRIP_STATUS[vehicle.status].label} · {vehicle.speedKmph} km/h
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        <RouteMap vehicles={vehicles} />
      </div>
    </section>
  );
}
